const React = require('react');
const T = require('prop-types');
const FieldGrid = require('./FieldGrid');
const FormField = require('./FormField');
const FormFieldUtils = require('./formFieldUtils');

const { useMemo } = React;

const internals = {};

module.exports = function FormFieldsRenderer(props) {

    const {
        columns = 2,
        componentRegistry,
        fieldProps,
        fields,
        formId,
        gap = 2,
        methods
    } = props;
    const segments = useMemo(() => FormFieldUtils.buildSegments(fields), [fields]);

    return <React.Fragment>
        {segments.map((segment, index) => internals.renderSegment({
            columns,
            componentRegistry,
            fieldProps,
            formId,
            gap,
            methods,
            segment,
            fallback: String(index)
        }))}
    </React.Fragment>;
};

module.exports.propTypes = {
    columns: T.number,
    componentRegistry: T.object,
    fieldProps: T.object,
    fields: T.array.isRequired,
    formId: T.string,
    gap: T.number,
    methods: T.object
};

internals.getFieldKey = ({
    field,
    fallback
}) => FormFieldUtils.getFieldKey({
    field,
    fallback
});

internals.renderSegment = ({
    columns,
    componentRegistry,
    fallback,
    fieldProps,
    formId,
    gap,
    methods,
    segment
}) => {

    if (segment.type === 'group') {
        const segmentFields = FormFieldUtils.normalizeFieldItems(segment);

        return <FieldGrid
            key={fallback}
            columns={columns}
            gap={gap}
        >
            {segmentFields.map((field, index) => internals.renderField({
                columns,
                componentRegistry,
                field,
                fieldProps,
                formId,
                gap,
                methods,
                fallback: `${fallback}-${index}`
            }))}
        </FieldGrid>;
    }

    return internals.renderField({
        columns,
        componentRegistry,
        field: segment.field,
        fieldProps,
        formId,
        gap,
        methods,
        fallback
    });
};

internals.staticRenderers = {
    custom: ({ field, key, methods }) => {

        if (typeof field.render !== 'function') {
            return null;
        }

        return <React.Fragment key={key}>{field.render({
            componentProps: field.componentProps || {},
            field,
            methods
        })}</React.Fragment>;
    },
    divider: ({ key }) => {

        const Divider = internals.getDivider();
        return <Divider style={{ margin: '14px 0' }} key={key} />;
    },
    explainer: ({ field, key }) => {

        const Typography = internals.getTypography();

        return <Typography
            key={key}
        variant='body1'
        sx={{
            color: 'text.primary',
            mb: 0,
            fontSize: 16
        }}
        >
            {field.text}
        </Typography>;
    },
    groupTitle: ({ field, key }) => {

        const Typography = internals.getTypography();

        return <Typography
            component='h3'
        key={key}
        sx={{
            alignSelf: 'center',
            bgcolor: 'background.paper',
            borderRadius: 999,
            color: 'text.primary',
            fontSize: 18,
            fontWeight: 700,
            lineHeight: '24px',
            mb: 0,
            mt: 1,
            px: 1.5,
            py: .25
        }}
        >
            {field.text}
        </Typography>;
    },
    spacer: ({ field, key }) => <div key={key} style={{ height: field.height || 16 }} />,
    title: ({ field, key }) => <h3 key={key} style={{ marginBottom: 0 }}>{field.text}</h3>
};

internals.staticRenderers.instructions = internals.staticRenderers.explainer;
internals.staticRenderers.group_title = internals.staticRenderers.groupTitle;

internals.renderStaticField = ({
    field,
    key,
    methods
}) => {

    const renderer = internals.staticRenderers[field.type];

    if (!renderer) {
        return undefined;
    }

    return renderer({
        field,
        key,
        methods
    });
};

internals.renderFieldGroup = ({
    columns,
    componentRegistry,
    field,
    fieldProps,
    formId,
    gap,
    key,
    methods
}) => {

    const fields = FormFieldUtils.normalizeFieldItems(field);

    if (!fields.length) {
        return null;
    }

    return <FieldGrid
        key={key}
        columns={field.columns || columns}
        gap={typeof field.gap === 'undefined' ? gap : field.gap}
    >
        {fields.map((child, index) => internals.renderField({
            columns: field.columns || columns,
            componentRegistry,
            field: child,
            fieldProps,
            formId,
            gap: typeof field.gap === 'undefined' ? gap : field.gap,
            methods,
            fallback: `${key}-${index}`
        }))}
    </FieldGrid>;
};

internals.renderField = ({
    columns,
    componentRegistry,
    field,
    fieldProps,
    formId,
    gap,
    methods,
    fallback
}) => {

    if (!field || field.type === 'break') {
        return null;
    }

    const key = internals.getFieldKey({
        field,
        fallback
    });
    const staticField = internals.renderStaticField({
        field,
        key,
        methods
    });

    if (typeof staticField !== 'undefined') {
        return staticField;
    }

    if (field.type === 'group') {
        return internals.renderFieldGroup({
            columns,
            componentRegistry,
            field,
            fieldProps,
            formId,
            gap,
            key,
            methods
        });
    }

    return <FormField
        key={key}
        columns={field.columns || columns}
        componentRegistry={componentRegistry}
        field={field}
        fieldProps={fieldProps}
        formId={formId}
        gap={gap}
    />;
};


internals.getDivider = () => {

    if (!internals.Divider) {
        internals.Divider = require('@mui/material/Divider').default;
    }

    return internals.Divider;
};

internals.getTypography = () => {

    if (!internals.Typography) {
        internals.Typography = require('@mui/material/Typography').default;
    }

    return internals.Typography;
};

module.exports.internals = internals;
